import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import useFetch from '../../../api/useFetch';
import ExoTable from '../../../components/exo/ExoTable';
import { useTranslation } from 'react-i18next';
import { Status } from '../../../components/special/Status';
import { Box } from '@mui/material';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { LeadStatusSelector } from '../status/LeadStatusSelector';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import { useNavigate } from 'react-router-dom';

export const LeadsPersons = forwardRef(({ isActive }, ref) => {
  const config = useConfig();
  const { apiData, setApiData } = useFetch('leads/persons');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    DBRequest({
      config,
      path: `leads/persons/${item.id}`,
      method: API_PATCH({ ...item, status }),
      onResponse: (data) => setApiData(updateOrCreate(apiData, data.data)),
    });
  }

  const columns = [
    {
      header: t('Status'), // header of column
      key: 'status', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Status
          text={row.status?.label || t('No Status')}
          variant={row.status?.variant || 'base'}
          statusColor={row.status?.color}
          onClick={(e) => handleClick(e, row)}
        />
      ),
    },
    {
      header: t('Name'), // header of column
      key: 'name', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      width: '100%',
      cell: ({ row }) => {
        const name = `${row.salutation} ${row.firstName} ${row.lastName}`;
        const nameShort = `${row.firstName} ${row.lastName}`;
        return (
          <Box className="flex flex-row justify-start items-center gap-2">
            <ExoAvatar name={nameShort} size="35px" />
            {name}
          </Box>
        );
      },
      onClick: (row) => navigate(`/leads/persons/${row.id}`),
    },
    {
      header: t('Responsible Employee'), // header of column
      key: 'name', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="flex flex-row justify-start items-center gap-2 whitespace-nowrap">
          <ExoAvatar
            picture={row.responsibleUser?.picture?.thumbnailUrl}
            name={`${row.responsibleUser?.lastName} ${row.responsibleUser?.firstName}`}
            size="35px"
          />
          {`${row.responsibleUser?.lastName} ${row.responsibleUser?.firstName}`}
        </Box>
      ),
    },
  ];
  // exposed functions
  const tableRef = useRef(null);
  useImperativeHandle(ref, () => ({
    setApiData: setApiData,
    addRow: handleCreate,
    addRows: (newRows) =>
      setApiData({
        ...apiData,
        data: [...apiData.data, ...newRows.data],
      }),
    updateRow: handleCreate,
    deleteRow: handleDelete,
  }));

  function handleCreate(data) {
    if (!tableRef) return;
    tableRef.current.addRow(data);
  }
  function handleDelete(data) {
    if (!tableRef) return;
    tableRef.current.deleteRow(data);
  }

  if (!isActive) return;
  return (
    <>
      <ExoTable
        ref={tableRef}
        data={apiData}
        isLoading={Boolean(!apiData)}
        columns={columns}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
      />
    </>
  );
});
